import { isAllowed } from '@/utils'

export const ONBOARDING_REQUIRED_PROPERTY = 'Onboarding required'
export const ONBOARDING_COMPLETED_PROPERTY = 'Onboarding completed'

const onboardingStatusCache = {}

function clientProperty (client, name) {
  if (!client?.properties) return null
  return client.properties.find(property => property?.type?.name === name) ?? null
}

function clientPropertyIsAllowed (client, name) {
  return isAllowed(clientProperty(client, name)?.value)
}

function isClientOnboardingCompleted (client) {
  return clientPropertyIsAllowed(client, ONBOARDING_COMPLETED_PROPERTY)
}

export function isClientOnboardingRequired (client) {
  return clientPropertyIsAllowed(client, ONBOARDING_REQUIRED_PROPERTY) && !isClientOnboardingCompleted(client)
}

export function isInitialOnboardingWizardPending (status) {
  return !!(status?.initial_wizard?.eligible && !status.initial_wizard.completed)
}

function isOnboardingEntrypointRoute (routeName) {
  return ['onboarding', 'onboarding-start'].includes(routeName)
}

export function onboardingRouteForStatus (status, currentRouteName = null) {
  // Always allowed routes
  if ([
    'account',
    'profile',
    'integrations-partners',
    'integrations',
    'integrations-shopify-request',
    'integrations-shopify-authorised',
    'integrations-shopify-subscription',
    'subscription'
  ].includes(currentRouteName)) {
    return null
  }

  if (!status || status.trial_expired) return null

  if (status.onboarding_completed) {
    return isOnboardingEntrypointRoute(currentRouteName)
      ? { name: 'dashboard' }
      : null
  }

  if (isInitialOnboardingWizardPending(status)) {
    return currentRouteName === 'onboarding-start'
      ? null
      : { name: 'onboarding-start' }
  }

  return currentRouteName === 'onboarding'
    ? null
    : { name: 'onboarding' }
}

export function cacheOnboardingStatus (clientHash, status) {
  if (!clientHash || !status) return
  onboardingStatusCache[String(clientHash)] = { data: status }
}

export function clearOnboardingStatusCache (clientHash = null) {
  if (clientHash) {
    delete onboardingStatusCache[String(clientHash)]
    return
  }

  Object.keys(onboardingStatusCache).forEach(key => delete onboardingStatusCache[key])
}

export async function getOnboardingStatus (api, clientHash) {
  if (!api || !clientHash) return null

  const key = String(clientHash)
  const cached = onboardingStatusCache[key]
  if (cached?.data) return cached.data
  if (cached?.promise) return cached.promise

  const request = api.get('/onboarding/status', clientHash)
    .then(response => {
      const status = response?.data || null
      if (status) {
        if (onboardingStatusCache[key]?.promise === request) {
          cacheOnboardingStatus(clientHash, status)
        }
      } else {
        clearOnboardingStatusCache(clientHash)
      }
      return status
    })
    .catch(error => {
      if (onboardingStatusCache[key]?.promise === request) {
        clearOnboardingStatusCache(clientHash)
      }
      return Promise.reject(error)
    })

  onboardingStatusCache[key] = { promise: request }

  return request
}
